import { type ExpandedFieldStateManager } from "./ExpandedFieldStateManager";
import { type TypeTagInfo, isNonExpandable } from "./TypeTagInfo";
import {
	type CollapsedValueRow,
	type ShallowField,
	type ValueInspectorRowInfo,
} from "./ValueInspectorRowInfo";
import { type ValuePath } from "./ValuePath";
import { traverseArrayValue } from "./traverseArrayValue";
import { traverseMapValue } from "./traverseMapValue";
import { traverseObjectValue } from "./traverseObjectValue";
import { traverseSetValue } from "./traverseSetValue";

export interface TraversalState {
	pathFromShallowestQueryRoot: ValuePath;
}

export function traverseValue(
	fieldExpander: ExpandedFieldStateManager,
	path: ValuePath,
	typeInfo: TypeTagInfo,
	shallow: boolean
): ValueInspectorRowInfo[] {
	if (isNonExpandable(typeInfo)) {
		return [
			{
				type: "NonExpandableValueRow",
				path,
				valueTypeInfo: typeInfo,
			},
		];
	}

	//
	// HALT traversal if collapsed. This is important for performance reasons.
	//

	if (!fieldExpander.expanded(path)) {
		return [collapsedValueRow(fieldExpander, path, shallow)];
	}

	return [
		{ type: "ExpandedValueOpeningRow", path },
		...traverseValueHelper(fieldExpander, path, shallow),
		{ type: "ExpandedValueClosingRow", path },
	];
}

function collapsedValueRow(
	fieldExpander: ExpandedFieldStateManager,
	path: ValuePath,
	shallow: boolean
): CollapsedValueRow {
	const children = shallow ? [] : traverseValueHelper(fieldExpander, path, true);

	const fields = children.map<ShallowField>((row) => {
		switch (row.type) {
			case "EmptyArraySlotValueRow":
				return {
					type: "EmptyArraySlotValueShallow",
					path: row.path,
					emptySlots: row.emptySlots,
				};
			case "NonExpandableValueRow":
				return {
					type: "NonExpandableValueShallow",
					path: row.path,
					valueTypeInfo: row.valueTypeInfo,
				};
			case "CollapsedValueRow":
			case "ExpandedValueOpeningRow":
			case "ExpandedValueClosingRow":
				return { type: "ExpandableValueShallow", path: row.path };
		}
	});

	return { type: "CollapsedValueRow", path, fields };
}

function traverseValueHelper(
	fieldExpander: ExpandedFieldStateManager,
	path: ValuePath,
	shallow: boolean
): ValueInspectorRowInfo[] {
	const lastComponent = path[path.length - 1];
	if (!lastComponent) {
		return [];
	}

	if (isNonExpandable(lastComponent.typeInfo)) {
		return [
			{
				type: "NonExpandableValueRow",
				path,
				valueTypeInfo: lastComponent.typeInfo,
			},
		];
	}

	switch (lastComponent.typeInfo.typeTag) {
		case "Map":
			return traverseMapValue(
				fieldExpander,
				path,
				lastComponent.value as Map<never, never>,
				shallow
			);

		case "Set":
			return traverseSetValue(
				fieldExpander,
				path,
				lastComponent.value as Set<never>,
				shallow
			);

		case "Arrayish":
			return traverseArrayValue(
				fieldExpander,
				path,
				lastComponent.value as Array<never>,
				shallow
			);

		case "any":
			return traverseObjectValue(fieldExpander, path, lastComponent.value as object, shallow);
	}
}
